import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ConnectionService } from 'ng-connection-service';
import { Subject, takeUntil } from 'rxjs';
import { SharedService } from 'src/app/core';

export type ConnectionStatus = 'ONLINE' | 'OFFLINE';

export interface ConnectionState {
  hasInternetAccess: boolean;
  hasNetworkConnection: boolean;
}

@Component({
  selector: 'app-internet-connection-status',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    @if (showStatus) {
      <div
        class="fixed left-0 right-0 top-0 z-[9999] flex items-center justify-between gap-2 px-2 py-2 shadow-lg transition-all duration-300 ease-out sm:px-6"
        [class.bg-emerald-600]="status === 'ONLINE'"
        [class.bg-rose-600]="status === 'OFFLINE'"
      >
        <div class="flex min-w-0 flex-1 items-center gap-2">
          @if (status === 'ONLINE') {
            <span
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/20"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                class="h-5 w-5 text-white"
                stroke="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.33309 8.07433C0.92156 8.44266 0.886539 9.07485 1.25487 9.48638C1.62319 9.89791 2.25539 9.93293 2.66691 9.5646L1.33309 8.07433ZM21.3331 9.5646C21.7446 9.93293 22.3768 9.89791 22.7451 9.48638C23.1135 9.07485 23.0784 8.44266 22.6669 8.07433L21.3331 9.5646ZM12 19C11.4477 19 11 19.4477 11 20C11 20.5523 11.4477 21 12 21V19ZM12.01 21C12.5623 21 13.01 20.5523 13.01 20C13.01 19.4477 12.5623 19 12.01 19V21ZM14.6905 17.04C15.099 17.4116 15.7315 17.3817 16.1031 16.9732C16.4748 16.5646 16.4448 15.9322 16.0363 15.5605L14.6905 17.04ZM18.0539 13.3403C18.4624 13.7119 19.0949 13.682 19.4665 13.2734C19.8381 12.8649 19.8082 12.2324 19.3997 11.8608L18.0539 13.3403ZM7.96372 15.5605C7.55517 15.9322 7.52524 16.5646 7.89687 16.9732C8.2685 17.3817 8.90095 17.4116 9.3095 17.04L7.96372 15.5605ZM4.60034 11.8608C4.19179 12.2324 4.16185 12.8649 4.53348 13.2734C4.90511 13.682 5.53756 13.7119 5.94611 13.3403L4.60034 11.8608ZM2.66691 9.5646C5.14444 7.34716 8.41371 6 12 6V4C7.90275 4 4.16312 5.54138 1.33309 8.07433L2.66691 9.5646ZM12 6C15.5863 6 18.8556 7.34716 21.3331 9.5646L22.6669 8.07433C19.8369 5.54138 16.0972 4 12 4V6ZM12 21H12.01V19H12V21ZM12 16C13.0367 16 13.9793 16.3931 14.6905 17.04L16.0363 15.5605C14.9713 14.5918 13.5536 14 12 14V16ZM12 11C14.3319 11 16.4546 11.8855 18.0539 13.3403L19.3997 11.8608C17.4466 10.0842 14.8487 9 12 9V11ZM9.3095 17.04C10.0207 16.3931 10.9633 16 12 16V14C10.4464 14 9.02872 14.5918 7.96372 15.5605L9.3095 17.04ZM5.94611 13.3403C7.54544 11.8855 9.66815 11 12 11V9C9.15127 9 6.55344 10.0842 4.60034 11.8608L5.94611 13.3403Z"
                  fill="#fff"
                ></path>
              </svg>
            </span>
          } @else {
            <span
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/20"
              aria-hidden="true"
            >
              <svg
                fill="#fff"
                class="h-5 w-5"
                viewBox="0 0 32 32"
                id="icon"
                xmlns="http://www.w3.org/2000/svg"
              >
                <title>wifi--off</title>
                <circle cx="16" cy="25" r="2"></circle>
                <path
                  d="M30,3.4141,28.5859,2,2,28.5859,3.4141,30,14.0962,19.3179a5.9359,5.9359,0,0,1,6.01,1.3193L21.52,19.2236a7.9669,7.9669,0,0,0-5.125-2.2041l3.3875-3.3877a11.9908,11.9908,0,0,1,4.5647,2.7647L25.76,14.9829A13.975,13.975,0,0,0,21.334,12.08L24.3308,9.083a17.9364,17.9364,0,0,1,4.2546,3.0747L30,10.7432v-.002a20.02,20.02,0,0,0-4.1895-3.1377Z"
                ></path>
                <path
                  d="M14.68,13.0776l2.0415-2.0415C16.481,11.0234,16.2437,11,16,11a13.9447,13.9447,0,0,0-9.771,3.9927l1.4136,1.4136A11.97,11.97,0,0,1,14.68,13.0776Z"
                ></path>
                <path
                  d="M16,7a17.87,17.87,0,0,1,4.2324.5254L21.875,5.8828A19.9537,19.9537,0,0,0,2,10.7412v.0225L3.4043,12.168A17.9193,17.9193,0,0,1,16,7Z"
                ></path>
              </svg>
            </span>
          }
          <p class="text-sm font-medium text-white sm:text-base">
            {{
              (status === 'ONLINE'
                ? 'CONNECTION_STATUS_ONLINE'
                : 'CONNECTION_STATUS_OFFLINE') | translate
            }}
          </p>
        </div>
        @if (status === 'OFFLINE') {
          <button
            type="button"
            (click)="reloadPage()"
            class="shrink-0 rounded-lg bg-white/20 px-2 py-1 text-sm font-semibold text-white transition hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
          >
            {{ 'CONNECTION_RELOAD_PAGE' | translate }}
          </button>
        }
      </div>
    }
  `,
})
export class InternetConnectionStatusComponent implements OnInit, OnDestroy {
  /** Current derived connection status shown in the banner UI. */
  status: ConnectionStatus = 'OFFLINE';

  /** Whether the app currently considers the client online. */
  isConnected = true;

  /** Controls banner visibility in the template. */
  showStatus = false;

  /** Auto-hide timer handle for the connection status banner. */
  private onlineHideTimeout: ReturnType<typeof setTimeout> | null = null;

  /** Component lifecycle signal to unsubscribe from streams safely. */
  private readonly destroy$ = new Subject<void>();

  /**
   * Previous connection state used to avoid showing the banner on initial load.
   * The banner should only appear when the connection status changes afterwards.
   */
  private previousConnected: boolean | null = null;

  constructor(
    private readonly connectionService: ConnectionService,
    private sharedService: SharedService
  ) {}

  ngOnInit(): void {
    this.startConnectionMonitoring();
  }

  /** Starts monitoring network/internet connectivity and controls the banner show/hide behavior. */
  private startConnectionMonitoring(): void {
    this.connectionService
      .monitor()
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: ConnectionState) => {
        // Online only when both network + internet are available.
        this.sharedService.hasNetworkConnection = !state.hasNetworkConnection;
        this.isConnected = state.hasNetworkConnection;
        this.status = this.isConnected ? 'ONLINE' : 'OFFLINE';

        const connectionChanged =
          this.previousConnected !== null &&
          this.previousConnected !== this.isConnected;
        this.previousConnected = this.isConnected;

        // Avoid showing the banner on initial emission; show only on subsequent changes.
        if (!connectionChanged) return;

        // If monitor emits repeatedly, don't keep cancelling the hide timer unless the state changed.
        this.clearOnlineHideTimeout();
        this.showStatus = true;
        this.onlineHideTimeout = setTimeout(() => {
          this.showStatus = false;
          this.onlineHideTimeout = null;
        }, 5000);
      });
  }

  /** Clears the banner auto-hide timer if it exists. */
  private clearOnlineHideTimeout(): void {
    if (this.onlineHideTimeout) {
      clearTimeout(this.onlineHideTimeout);
      this.onlineHideTimeout = null;
    }
  }

  /** Reloads the page (useful after network reconnect). */
  reloadPage(): void {
    window.location.reload();
  }

  /** Lifecycle cleanup: clear timers and unsubscribe from observables. */
  ngOnDestroy(): void {
    this.clearOnlineHideTimeout();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
